<template>
  <div class="add-invoice-body fade-in">

    <!-- PAGE LOADER -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="spinner"></div>
        <p>Loading data, please wait...</p>
      </div>
    </transition>

    <!-- HEADER -->
    <div class="section-header" v-if="!loading">
      <div class="title-area">
        <h2>Create New Invoice</h2>
        <p>Manage, schedule, and send invoices effortlessly.</p>
      </div>
      <!-- <button class="btn-help"><i class="bx bx-help-circle"></i> Help</button> -->
    </div>

    <!-- MAIN LAYOUT -->
    <div class="invoice-layout" v-if="!loading">

      <!-- LEFT MAIN AREA -->
      <div class="main-pane">

        <!-- CLIENT INFO -->
        <div class="card-section animate-in">
          <h3><i class="bx bx-user"></i> Client Information</h3>

          <div v-if="clientsLoading" class="loader-inline">Fetching clients...</div>

          <div class="form-grid" v-else>
            <div class="form-group full">
              <label>Select Client</label>
              <div class="client-select">
                <select v-model="form.client">
                  <option value="">Choose Client</option>
                  <option v-for="client in clients" :key="client.id" :value="client.id">
                    {{ client.name }}
                  </option>
                </select>
                <button class="btn-outline" style="margin-top: 1rem;" @click="openClientModal">
                  <i class="bx bx-plus"></i> Add New
                </button>
              </div>
            </div>
          </div>

          <!-- Selected Client Preview -->
          <transition name="fade-slide">
            <div v-if="form.client" class="client-preview" style="margin-top: 1rem;">
              <p><b>Email:</b> {{ selectedClient.email }}</p>
              <p><b>Phone:</b> {{ selectedClient.phone }}</p>
              <p><b>Address:</b> {{ selectedClient.address }}</p>
            </div>
          </transition>
        </div>

        <!-- INVOICE DETAILS -->
        <div class="card-section animate-in">
          <h3><i class="bx bx-receipt"></i> Invoice Details</h3>
          <div class="form-grid two-col">
            <div class="form-group">
              <label>Invoice No.</label>
              <input v-model="form.invoiceRef" placeholder="'INV-0001'" />
            </div>
            <div class="form-group">
              <label>Date</label>
              <input type="date" v-model="form.date" />
            </div>
            <div class="form-group">
              <label>Due Date</label>
              <input type="date" v-model="form.dueDate" />
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status">
                <option>Pending</option>
                <option>Paid</option>
                <option>Overdue</option>
              </select>
            </div>
          </div>
        </div>

        <!-- LINE ITEMS -->
        <div class="card-section animate-in">
          <div class="section-header-mini">
            <h3><i class="bx bx-list-plus"></i> Line Items</h3>
            <button class="btn-black-sm" @click="addItemRow">
              <i class="bx bx-plus"></i> Add Item
            </button>
          </div>

          <div v-if="productsLoading" class="loader-inline">Fetching products...</div>

          <transition-group name="fade-slide" tag="div" v-else style="margin-top: 1rem;">
            <div v-for="(item, i) in form.items" :key="i" class="item-card-dual">
              <!-- Top Row -->
              <div class="item-top-row">
                <div class="custom-select">
                  <select v-model="item.name" @change="handleItemSelect($event, i)">
                    <option value="">Select Product</option>
                    <option v-for="p in products" :key="p.id" :value="p.name">{{ p.name }}</option>
                  </select>
                </div>

                <div class="item-fields">
                  <input v-model.number="item.qty" type="number" min="1" placeholder="Qty" />
                  <input v-model.number="item.rate" type="number" min="0" placeholder="Rate" />
                  <span class="amount">
                    $
                    {{ computeItemTotal(item).toFixed(2) }}
                  </span>
                </div>
              </div>

              <!-- Bottom Row -->
              <div class="item-bottom-row">
                <div class="tax-mini">
                  <label>Tax:</label>
                  <select v-model="item.taxType">
                    <option value="none">None</option>
                    <option value="percent">%</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  <input v-model.number="item.taxValue" type="number" min="0" placeholder="0" />
                </div>

                <div class="discount-mini">
                  <label>Discount:</label>
                  <select v-model="item.discountType">
                    <option value="none">None</option>
                    <option value="percent">%</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  <input v-model.number="item.discountValue" type="number" min="0" placeholder="0" />
                </div>

                <button class="btn-remove" @click="removeItem(i)">
                  <i class="bx bx-trash"></i> Remove
                </button>
              </div>
            </div>
          </transition-group>

          <!-- EMPTY STATE -->
          <div v-if="!form.items.length" class="empty-placeholder">
            <i class="bx bx-box"></i>
            <p>No items added yet</p>
          </div>

          <div class="add-product-row">
            <button class="btn-outline-sm" @click="openProductModal">
              <i class="bx bx-plus-circle"></i> Add New Product
            </button>
          </div>
        </div>

        <!-- SUMMARY -->
        <div class="card-section summary animate-in">
          <h3><i class="bx bx-calculator"></i> Summary</h3>

          <div class="form-group" style="margin-top: 1rem;">
            <label style="color: #fff;">Add Tax</label>
            <div class="tax-row">
              <select v-model="form.taxType">
                <option value="none">None</option>
                <option value="fixed">Fixed</option>
                <option value="percent">Percentage</option>
              </select>
              <input type="number" placeholder="0" v-model.number="form.taxValue" />
              <span v-if="form.taxType === 'percent'">%</span>
              <span v-else-if="form.taxType === 'fixed'">$</span>
            </div>
          </div>

          <div class="form-group" style="margin-top: 1rem;">
            <label style="color: #fff;">Discount</label>
            <div class="tax-row">
              <select v-model="form.discountType">
                <option value="none">None</option>
                <option value="fixed">Fixed</option>
                <option value="percent">Percentage</option>
              </select>
              <input type="number" placeholder="0" v-model.number="form.discountValue" />
              <span v-if="form.discountType === 'percent'">%</span>
              <span v-else-if="form.discountType === 'fixed'">$</span>
            </div>
          </div>

          <div class="form-group" style="margin-top: 1rem;">
            <label style="color: #fff;">Other Amount</label>
            <input type="number" placeholder="Enter additional charges" v-model.number="form.otherAmount" />
          </div>

          <!-- Totals -->
          <div class="summary-row" style="margin-top: 1rem;">
            <span>Subtotal</span><strong>${{ subtotal.toFixed(2) }}</strong>
          </div>
          <div class="summary-row" style="margin-top: 1rem;">
            <span>Tax</span><strong>${{ computedTax.toFixed(2) }}</strong>
          </div>
          <div class="summary-row" style="margin-top: 1rem;">
            <span>Discount</span><strong>- ${{ computedDiscount.toFixed(2) }}</strong>
          </div>
          <div class="summary-row" style="margin-top: 1rem;">
            <span>Other</span><strong>${{ form.otherAmount.toFixed(2) }}</strong>
          </div>
          <div class="summary-row total" style="margin-top: 1rem;">
            <span>Total</span><strong>${{ finalTotal.toFixed(2) }}</strong>
          </div>
        </div>

        <!-- NOTES -->
        <div class="card-section animate-in" style="margin-top: 1rem;">
          <h3><i class="bx bx-edit"></i> Notes</h3>
          <textarea v-model="form.notes" placeholder="Add additional information..." rows="3"></textarea>
        </div>
      </div>

      <!-- RIGHT SIDEBAR -->
      <div class="side-pane">
        <div class="card-section sticky animate-in">
          <button class="btn-primary full btn-center" @click="submitInvoice('send')">
            <i class="bx bx-paper-plane"></i>
            <span>Send Invoice</span>
          </button>


          <button class="btn-outline full" style="margin-top: 1rem;" @click="openPreview">
            <i class="bx bx-show"></i> Preview
          </button>

          <button class="btn-outline full" style="margin-top: 1rem;" @click="submitInvoice('draft')">
            <i class="bx bx-save"></i> Save Draft
          </button>

          <div class="divider"><span>or</span></div>

          <h4>Schedule for Later</h4>
          <div class="form-group" style="margin-top: 1rem;">
            <label>Date</label>
            <input type="date" v-model="schedule.date" />
          </div>
          <div class="form-group" style="margin-top: 1rem;">
            <label>Time</label>
            <input type="time" v-model="schedule.time" />
          </div>

          <button class="btn-black full" style="margin-top: 1rem;" @click="submitInvoice('schedule')">
            <i class="bx bx-calendar-check"></i> Schedule Now
          </button>

          <button class="btn-outline full mt-4" style="margin-top: 1rem;" @click="openSettingsDrawer">
            <i class="bx bx-cog"></i> Settings
          </button>
        </div>
      </div>
    </div>

    <!-- SETTINGS DRAWER -->
    <transition name="slide-drawer">
      <div v-if="showSettings" class="settings-drawer">
        <div class="drawer-header">
          <h3><i class="bx bx-cog"></i> Invoice Settings</h3>
          <button @click="closeSettingsDrawer" class="close-btn"><i class="bx bx-x"></i></button>
        </div>
        <div class="drawer-content">
          <!-- Payment -->
          <section class="payment-connect">
            <h4><i class="bx bx-credit-card"></i> Accept Online Payments</h4>
            <p style="margin-top: 0.8rem;">Let clients pay online securely using any of the supported gateways below.</p>
            <div class="gateway-row">
              <button class="gateway-btn stripe"><i class="bx bxl-stripe"></i> Stripe</button>
              <button class="gateway-btn paypal"><i class="bx bxl-paypal"></i> PayPal</button>
              <!-- <button class="gateway-btn razorpay"><i class="bx bx-wallet-alt"></i> Razorpay</button> -->
            </div>
          </section>

          <!-- Branding -->
          <section class="branding-settings">
            <h4><i class="bx bx-palette"></i> Customize Style</h4>
            <div class="branding-grid">
              <div class="upload-card" @click="triggerUpload">
                <input type="file" ref="fileInput" @change="handleFile" style="display: none" />
                <template v-if="!form.image">
                  <div class="upload-icon"><i class="bx bx-image-add"></i></div>
                  <p>Upload Logo</p>
                </template>
                <img v-else :src="apiBaseUrl+form.image" alt="Logo" />
              </div>

              <div class="color-card">
                <label>Theme Color</label>
                <div class="color-picker">
                  <div class="color-preview" :style="{ backgroundColor: form.color }"></div>
                  <input type="color" v-model="form.color" />
                  <span class="color-code">{{ form.color.toUpperCase() }}</span>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4>Recurring Invoices</h4>
            <p style="margin-top: 1rem;">Automatically generate invoices on a set schedule.</p>
            <select  v-model="form.recurring_type" style="margin-top: 1rem;">
              <option value="" selected>None</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Yearly">Yearly</option>
            </select>
          </section>
        </div>
      </div>
    </transition>

    <!-- ADD PRODUCT MODAL -->
    <!-- ADD PRODUCT MODAL -->
<transition name="fade-slide">
  <div v-if="showAddProductModal" class="modal-overlay" @click.self="closeProductModal">
    <div class="modal-card">
      <h3><i class="bx bx-box"></i> Add New Product / Item</h3>

      <div class="form-group">
        <label>Item Name <span style="color:red">*</span></label>
        <input v-model="newProduct.item_name" placeholder="Enter item name" />
      </div>

      <div class="form-group">
        <label>Description</label>
        <textarea v-model="newProduct.item_desc" placeholder="Short description..." rows="2"></textarea>
      </div>

      <div class="form-grid two-col">
        <div class="form-group">
          <label>Price</label>
          <input v-model.number="newProduct.item_price" type="number" placeholder="0.00" />
        </div>
        <div class="form-group">
          <label>Unit</label>
          <input v-model="newProduct.item_unit" placeholder="pcs / hrs / box" />
        </div>
      </div>

      <div class="form-grid two-col">
        <div class="form-group">
          <label>Tax (%)</label>
          <input v-model.number="newProduct.item_tax" type="number" placeholder="0" />
        </div>
        <div class="form-group">
          <label>Discount (%)</label>
          <input v-model.number="newProduct.item_discount" type="number" placeholder="0" />
        </div>
      </div>

      <div class="form-group">
        <label>Category</label>
        <input v-model="newProduct.item_category" placeholder="Category name" />
      </div>

      <div class="form-group">
        <label>Status</label>
        <select v-model="newProduct.item_status">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div class="modal-actions">
        <button class="btn-primary" @click="addNewProduct">Save Item</button>
        <button class="btn-outline" @click="closeProductModal">Cancel</button>
      </div>
    </div>
  </div>
</transition>


    <!-- ADD CLIENT MODAL -->
    <transition name="fade-slide">
      <div v-if="showAddClientModal" class="modal-overlay" @click.self="closeClientModal">
        <div class="modal-card">
          <h3><i class="bx bx-user-plus"></i> Add New Client</h3>
          <div class="form-group">
            <label>First Name</label>
            <input v-model="newClient.fname" placeholder="Client name" />
          </div>
          <div class="form-group">
            <label>Last Name</label>
            <input v-model="newClient.lname" placeholder="Client name" />
          </div>
          <div class="form-group">
            <label>Email</label>
            <input v-model="newClient.email" placeholder="client@email.com" />
          </div>
          <div class="form-group">
            <label>Phone</label>
            <input v-model="newClient.phone" placeholder="+91 00000 00000" />
          </div>
          <div class="form-group">
            <label>Address</label>
            <textarea v-model="newClient.address" rows="2"></textarea>
          </div>
          <div class="modal-actions">
            <button class="btn-primary" @click="addNewClient">Save Client</button>
            <button class="btn-outline" @click="closeClientModal">Cancel</button>
          </div>
        </div>
      </div>
    </transition>
   <transition name="fade">
  <div v-if="showPreview" class="preview-fullscreen" @click.self="closePreview">
    <div class="preview-content">
      <button class="preview-close-btn" @click="closePreview">
        ✕
      </button>

      <UniversalPreviewPrint
        :record="previewRecord"
        :branding="previewBranding"
        :mainType="mtype"
      />
    </div>
  </div>
</transition>

  </div>
</template>
<script setup>
import { ref, computed, onMounted,nextTick } from "vue";
import { useToast } from "vue-toastification";
import { createInvoice, uploadFile,getTableRowCount,getInvoiceById } from "@/apis/invoiceapis";
import { getItems, addItem } from "@/apis/itemapis";
import { getClients, addClient } from "@/apis/clientapis";
import { apiBaseUrl } from "@/apis/appconst";
import UniversalPreviewPrint from "@/comps/UniversalPreviewPrint.vue";

const showPreview = ref(false);

const previewRecord = ref({});
const previewBranding = ref({});
const mtype = ref("invoice");

const toast = useToast();

/* === STATE === */
const loading = ref(true);
const productsLoading = ref(false);
const clientsLoading = ref(false);
const isSubmitting = ref(false);

/* === FORM === */
const form = ref({
  id:"",
  finalTotal: 0,
  color: "#111827",
  rowCnt: 1,
  image: "",
  client: "",
  recurring_type:"",
  client_id:"",
  invoiceRef: "INV-00011",
  date: "",
  dueDate: "",
  status: "Pending",
  notes: "",
  items: [
    {
      name: "",
      qty: 1,
      rate: 0,
      taxType: "none",
      taxValue: 0,
      discountType: "none",
      discountValue: 0,
    },
  ],
  currency: "USD",
  taxType: "none",
  taxValue: 0,
  discountType: "none",
  discountValue: 0,
  otherAmount: 0,
});

function closePreview() {
  showPreview.value = false;
}


function openPreview() {
  previewRecord.value = {
    number: form.value.invoiceRef || "",
    date: form.value.date || "",
    due_date: form.value.dueDate || "",

    client_name: selectedClient.value.name || "",
    client_email: selectedClient.value.email || "",
    client_mobile: selectedClient.value.phone || "",
    client_address: selectedClient.value.address || "",

    subtotal: subtotal.value,
    discount_total: computedDiscount.value,
    tax_total: computedTax.value,
    other_amount: form.value.otherAmount || 0,
    total: finalTotal.value,

    notes: form.value.notes || "",

    items: form.value.items.map((it) => {
      const base = (Number(it.qty) || 0) * (Number(it.rate) || 0);
      const discount = computeItemDiscount(it);
      const tax = computeItemTax(it);

      return {
        name: it.name || "",
        qty: it.qty || 1,
        rate: it.rate || 0,
        base_amount: base,
        discount_amount: discount,
        tax_amount: tax,
        final_total: computeItemTotal(it),
      };
    }),
  };

  previewBranding.value = {
    logo: apiBaseUrl+form.value.image || "",
    title: "Invoice",          // You have no businessName field
    email: "",                 // You have no businessEmail field
    phone: "",                 // You have no businessPhone field
    address: "",               // You have no businessAddress field
    primary: form.value.color || "#111827",
    secondary: "#6b7280",
  };

  showPreview.value = true;
}



/* === DATA === */
const clients = ref([]);
const products = ref([]);
const schedule = ref({ date: "", time: "" });
const showSettings = ref(false);
const fileInput = ref(null);

/* === CLIENT MODAL === */
const showAddClientModal = ref(false);
const newClient = ref({ fname: "",lname:"", email: "", phone: "", address: "" });

/* === PRODUCT MODAL === */
const showAddProductModal = ref(false);
const newProduct = ref({
  item_name: "",
  item_desc: "",
  item_price: 0,
  item_unit: "",
  item_tax: "",
  item_discount: "",
  item_category: "",
  item_status: "active",
});


/* === UTILS === */
const triggerUpload = () => fileInput.value.click();

const handleFile = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const fd = new FormData();
  fd.append("file1", file);

  toast.success("Uploading logo...");
  const res = await uploadFile(fd);
  if (res?.status && res.file1) {
    form.value.image = res.file1;
    toast.success("Logo uploaded successfully");
  } else {
    toast.error("Logo upload failed");
  }
};

/* === CLIENTS === */
const loadClients = async () => {
  try {
    clientsLoading.value = true;
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    const res = await getClients(userData.user_id, "", 0, "");
    if (res?.status && res.records?.length) {
      clients.value = res.records.map((r) => ({
        id: r.user_id,
        name: `${r.fname} ${r.lname}`,
        email: r.email,
        phone: r.mobile,
        address: r.address_line1,
      }));
    } else clients.value = [];
  } catch (e) {
    console.error(e);
  } finally {
    clientsLoading.value = false;
  }
};

const loadRows = async () => {
  try {
    const res1 = await getTableRowCount("invoice");
    if (res1?.status) {
      form.value.rowCnt = parseInt(res1.total_rows+"") || 0;
      form.value.rowCnt++;
      form.value.invoiceRef = "INV-0001"+form.value.rowCnt;
    }
    } catch (e) {
    console.error(e);
  } finally {
    productsLoading.value = false;
  }
}

/* === ITEMS === */
const loadProducts = async () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    productsLoading.value = true;
    const res = await getItems(0,userData.user_id+"", "");
    if (res?.status && res.records?.length) {
      products.value = res.records.map((r) => ({
        id: r.item_id,
        name: r.item_name,
        rate: parseFloat(r.item_unit_price) || 0,
      }));

    } else products.value = [];

  

  } catch (e) {
    console.error(e);
  } finally {
    productsLoading.value = false;
  }
};

/* === MODAL HANDLERS === */
const openClientModal = () => (showAddClientModal.value = true);
const closeClientModal = () => (showAddClientModal.value = false);
const openProductModal = () => (showAddProductModal.value = true);
const closeProductModal = () => (showAddProductModal.value = false);

/* === ADD NEW CLIENT === */
const addNewClient = async () => {
  if (!newClient.value.fname || !newClient.value.lname || !newClient.value.email) {
    toast.warning("Client name and email are required");
    return;
  }
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const payload = {
    fname: newClient.value.fname,
    lname: newClient.value.lname,
    email: newClient.value.email,
    mobile: newClient.value.phone,
    address: newClient.value.address,
   
    user_cre_by: userData.user_id || "",
  };
  toast.success("Saving client...");
  const res = await addClient(payload);
  if (res?.status) {
    toast.success("Client added successfully");
    await loadClients();
    closeClientModal();
  } else {
    toast.error("Failed to add client");
  }
};

/* === ADD NEW PRODUCT (Now API Integrated) === */
const addNewProduct = async () => {
  
  if (!newProduct.value.item_name || !newProduct.value.item_name.trim()) {
    toast.warning("Item name is required");
    return;
  }
  if (!newProduct.value.item_unit || !newProduct.value.item_unit.trim()) {
    toast.warning("Please enter unit (e.g., pcs, box, hr)");
    return;
  }
  if (newProduct.value.item_price === null || newProduct.value.item_price === "" || isNaN(newProduct.value.item_price)) {
    toast.warning("Valid item price is required");
    return;
  }
  if (parseFloat(newProduct.value.item_price) <= 0) {
    toast.warning("Item price must be greater than 0");
    return;
  }
  if (newProduct.value.item_tax !== "" && (isNaN(newProduct.value.item_tax) || parseFloat(newProduct.value.item_tax) < 0)) {
    toast.warning("Tax must be a valid non-negative number");
    return;
  }

  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const payload = {
    item_name: newProduct.value.item_name,
    item_desc: newProduct.value.item_desc,
    item_price: newProduct.value.item_price || 0,
    item_unit: newProduct.value.item_unit,
    item_tax: newProduct.value.item_tax || 0,
    item_discount: newProduct.value.item_discount || 0,
    item_category: newProduct.value.item_category,
    item_status: newProduct.value.item_status,
    item_by: userData.user_id || "",
  };

  toast.success("Saving item...");
  const res = await addItem(payload);

  if (res?.status) {
    toast.success(res.success || "Item created successfully");
    await loadProducts(); // refresh list
    closeProductModal();

    // Reset form
    newProduct.value = {
      item_name: "",
      item_desc: "",
      item_price: 0,
      item_unit: "",
      item_tax: "",
      item_discount: "",
      item_category: "",
      item_status: "active",
    };
  } else {
    toast.error(res?.success || "Failed to save item");
  }
};


/* === COMPUTED CLIENT === */
const selectedClient = computed(() => {
  
  return clients.value.find((c) => c.id === form.value.client) || {};
});


/* === CALCULATION HELPERS === */
const computeItemTax = (item) => {
  const qty = Number(item.qty) || 0;
  const rate = Number(item.rate) || 0;
  const val = Number(item.taxValue) || 0;
  if (item.taxType === "percent") return (qty * rate * val) / 100;
  if (item.taxType === "fixed") return val;
  return 0;
};
const computeItemDiscount = (item) => {
  const qty = Number(item.qty) || 0;
  const rate = Number(item.rate) || 0;
  const val = Number(item.discountValue) || 0;
  if (item.discountType === "percent") return (qty * rate * val) / 100;
  if (item.discountType === "fixed") return val;
  return 0;
};

/* === ITEM ACTIONS === */
const addItemRow = () =>
  form.value.items.push({
    name: "",
    qty: 1,
    rate: 0,
    taxType: "none",
    taxValue: 0,
    discountType: "none",
    discountValue: 0,
  });

const removeItem = (i) => form.value.items.splice(i, 1);

const handleItemSelect = (event, index) => {
  const name = event.target.value;
  const selected = products.value.find(p => p.name === name);
  form.value.items[index].rate = selected ? Number(selected.rate) : 0;
};

const computeItemBase = (item) => {
  return (Number(item.qty) || 0) * (Number(item.rate) || 0);
};

const computeItemTotal = (item) => {
  const base = computeItemBase(item);
  const tax = computeItemTax(item);
  const discount = computeItemDiscount(item);
  return Math.max(base + tax - discount, 0);
};

const subtotal = computed(() => {
  return form.value.items.reduce((t, item) => {
    return t + computeItemTotal(item);
  }, 0);
});


const computedTax = computed(() => {
  const val = Number(form.value.taxValue) || 0;
  if (form.value.taxType === "percent") return (subtotal.value * val) / 100;
  if (form.value.taxType === "fixed") return val;
  return 0;
});

const computedDiscount = computed(() => {
  const val = Number(form.value.discountValue) || 0;
  if (form.value.discountType === "percent") return (subtotal.value * val) / 100;
  if (form.value.discountType === "fixed") return val;
  return 0;
});




const finalTotal = computed(() => {
  const other = Number(form.value.otherAmount) || 0;
  return Math.max(
    subtotal.value + computedTax.value - computedDiscount.value + other,
    0
  );
});


/* === VALIDATIONS === */
const validateInvoice = () => {
  if (!form.value.client) return "Please select a client.";
  if (!form.value.invoiceRef.trim()) return "Invoice number is required.";
  if (!form.value.date) return "Invoice date is required.";
  if (!form.value.dueDate) return "Due date is required.";
  if (!form.value.items.length) return "Please add at least one item.";

  for (const item of form.value.items) {
    if (!item.name) return "Each item must have a name.";
    if (!item.qty || item.qty <= 0) return "Quantity must be greater than zero.";
    if (isNaN(item.rate)) return "Item rate must be a valid number.";
  }

  if (finalTotal.value <= 0) return "Total amount must be greater than zero.";

  return null;
};

/* === SETTINGS === */
const openSettingsDrawer = () => (showSettings.value = true);
const closeSettingsDrawer = () => (showSettings.value = false);

/* === SUBMIT INVOICE === */
const submitInvoice = async (actionType = "send") => {
  // ✅ 1. Validate mandatory invoice fields
  const error = validateInvoice();
  if (error) {
    toast.warning(error);
    return;
  }

  // ✅ 2. Extra validation for scheduling
  if (actionType === "schedule") {
    if (!schedule.value.date || !schedule.value.time) {
      toast.warning("Please select both schedule date and time before scheduling.");
      return;
    }

    const now = new Date();
    const scheduledDateTime = new Date(`${schedule.value.date}T${schedule.value.time}`);

    if (scheduledDateTime <= now) {
      toast.warning("Schedule date and time must be in the future.");
      return;
    }
  }

  try {
    isSubmitting.value = true;
    loading.value = true;
    toast.success("Processing invoice...");

    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

    const payload = {
  invoice_id: form.value.id,
  invoice_number: form.value.invoiceRef,
  invoice_client_id: form.value.client,

  // Global calculations
  invoice_global_tax_type: form.value.taxType,
  invoice_global_tax_value: form.value.taxValue,
  invoice_global_discount_type: form.value.discountType,
  invoice_global_discount_value: form.value.discountValue,
  invoice_other_amount: form.value.otherAmount,
  invoice_final_total: finalTotal.value,

  // Basic invoice
  invoice_total_amount: finalTotal.value,
  invoice_status:
    actionType === "draft"
      ? "Draft"
      : actionType === "schedule"
      ? "Scheduled"
      : form.value.status,
  invoice_currency: form.value.currency,
  invoice_due_date: form.value.dueDate,
  invoice_date: form.value.date,
  invoice_theme_color: form.value.color,
  invoice_logo: form.value.image,
  invoice_notes: form.value.notes,
  invoice_recurring_type:form.value.recurring_type,
  invoice_is_recurring:form.value.recurring_type.length>0?"1":"0",

  // User
  invoice_created_by: userData.user_id,

  // Items
  items: form.value.items.map(it => ({
  ...it,
  base_amount: computeItemBase(it),
  tax_amount: computeItemTax(it),
  discount_amount: computeItemDiscount(it),
  total_amount: computeItemTotal(it),
})),

  // Schedule
  invoice_schedule_date: schedule.value.date,
  invoice_schedule_time: schedule.value.time,
};


    const res = await createInvoice("invoice", payload);

    if (res?.status) {
      toast.success(res.success || "Invoice saved successfully!");
      window.location.href = "/invoices?type=invoices";
    } else {
      toast.error(res?.success || "Failed to save invoice");
    }
  } catch (err) {
    console.error("submitInvoice error:", err);
    toast.error("Unexpected error while saving invoice");
  } finally {
    loading.value = false;
    isSubmitting.value = false;
  }
};



/* === LIFECYCLE === */
onMounted(async () => {
  try {
    loading.value = true;

    // Load base data first (clients + products + rows if any)
    await Promise.all([loadClients(), loadProducts(), loadRows?.()]);

    const urlParams = new URLSearchParams(window.location.search);
    const invoiceId = urlParams.get("id");

    if (invoiceId) {

      const res = await getInvoiceById("invoice", invoiceId);

      if (res?.status && res.record) {
        const inv = res.record;

        // ✅ Fill invoice form details
        form.value.id = invoiceId;
        form.value.invoiceRef = inv.invoice_number || "";
        form.value.client = inv.invoice_client_name || "";
        form.value.client_id = inv.invoice_client_id || "";
        form.value.date = inv.invoice_date || "";
        form.value.recurring_type = inv.invoice_recurring_type || "";
        form.value.dueDate = inv.invoice_due_date || "";
        form.value.status = inv.invoice_status || "Pending";
        form.value.currency = inv.invoice_currency || "USD";
        form.value.notes = inv.invoice_notes || "";
        form.value.color = inv.invoice_theme_color || "#111827";
        form.value.image = apiBaseUrl+inv.invoice_logo || "";
        form.value.taxValue = parseFloat(inv.invoice_tax_value || 0);
        form.value.taxType = inv.invoice_global_tax_type || "none";
        form.value.discountType = inv.invoice_global_discount_type || "none";
        form.value.discountValue = parseFloat(inv.invoice_global_discount_value || 0);
        form.value.otherAmount = parseFloat(inv.invoice_other_amount || 0);
        form.value.finalTotal = parseFloat(inv.invoice_final_total || 0);


        // ✅ Load line items if exist
        if (Array.isArray(inv.items) && inv.items.length > 0) {
          form.value.items = inv.items.map((it) => ({
            name: it.invoice_item_name || "",
            qty: parseFloat(it.invoice_item_qty || 1),
            rate: parseFloat(it.invoice_item_rate || 0),
            taxType: it.invoice_item_tax_type || "none",
            taxValue: parseFloat(it.invoice_item_tax_value || 0),
            discountType: it.invoice_item_discount_type || "none",
            discountValue: parseFloat(it.invoice_item_discount_value || 0),
          }));
        } else {
          form.value.items = [
            {
              name: "",
              qty: 1,
              rate: 0,
              taxType: "none",
              taxValue: 0,
              discountType: "none",
              discountValue: 0,
            },
          ];
        }

        // ✅ Auto recalc totals
        await nextTick();
      }
    }
  } catch (err) {
    console.error("onMounted error:", err);
  } finally {
    loading.value = false;
  }
});

</script>


<style scoped>

.btn-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
}

.btn-center i {
  font-size: 1.1rem;
  line-height: 1;
}

.payment-connect {
  margin-top: 1.5rem;
}

.payment-connect h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: #111827;
}

.gateway-row {
  display: flex;
  gap: 0.8rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.gateway-btn {
  flex: 1 1 calc(33.33% - 0.5rem);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  color: #fff;
  font-weight: 600;
  padding: 0.7rem 0.6rem;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.25s ease;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
}

/* Individual brand styles */
.gateway-btn.stripe {
  background: #635bff;
}
.gateway-btn.paypal {
  background: #00457c;
}
.gateway-btn.razorpay {
  background: #0b64f0;
}

.gateway-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Razorpay uses SVG logo instead of icon */
.gateway-btn.razorpay img {
  height: 18px;
  filter: brightness(0) invert(1);
}

/* Adjust icons */
.gateway-btn i {
  font-size: 1.2rem;
}

.add-invoice-body {
  background: #fff;
  border-radius: 1.5rem;
  padding: 2rem 2.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
}

/* === Add Product Row (below line items) === */
.add-product-row {
  margin-top: 1rem;
  text-align: right;
  display: flex;
  justify-content: flex-end;
}

/* === Add New Product Button === */
.btn-outline-sm {
  background: #fff;
  color: #111827;
  border: 1px solid #111827;
  border-radius: 8px;
  padding: 0.55rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.25s ease;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.08);
}

.btn-outline-sm i {
  font-size: 1rem;
  color: #111827;
  transition: transform 0.3s ease;
}

.btn-outline-sm:hover {
  background: #111827;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.15);
}

.btn-outline-sm:hover i {
  color: #fff;
  transform: rotate(90deg);
}


/* === HEADER === */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
}
.section-header h2 {
  font-size: 1.9rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.3rem;
}
.section-header p {
  color: #6b7280;
}
.btn-help {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  border: none;
  padding: 0.7rem 1.3rem;
  cursor: pointer;
  transition: all 0.25s ease;
}
.btn-help:hover {
  background: #1f2937;
  transform: translateY(-1px);
}

/* === LAYOUT === */
.invoice-layout {
  display: flex;
  gap: 2.2rem;
}
.main-pane {
  width: 68%;
}
.side-pane {
  width: 32%;
}

/* === CARD SECTION === */
.card-section {
  background: #f9fafb;
  border-radius: 1rem;
  padding: 1.8rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}
.card-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
}
.card-section h3 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.section-header-mini {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* === FORM GRID === */
.form-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.form-grid.two-col .form-group {
  flex: 1 1 calc(50% - 1rem);
}
.form-group.full {
  flex: 1 1 100%;
}
label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 0.3rem;
  display: block;
}
input,
select,
textarea {
  width: 100%;
  padding: 0.8rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  transition: border 0.3s ease;
}
input:focus,
select:focus,
textarea:focus {
  border-color: #111827;
  outline: none;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}
textarea {
  resize: none;
}

/* === LINE ITEMS === */
.item-row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 0.8fr 1fr 40px;
  gap: 0.8rem;
  align-items: center;
  margin-bottom: 0.8rem;
  padding: 0.8rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}
.amount {
  text-align: right;
  font-weight: 600;
  color: #111827;
}
.btn-remove {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.2s ease;
}
.btn-remove:hover {
  transform: scale(1.15);
  color: #dc2626;
}

/* === SUMMARY SECTION === */
.summary {
  background: #111827;
  color: #fff;
  border-radius: 16px;
  padding: 1.8rem;
}
.summary h3 {
  color: #fff;
  font-size: 1.15rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.6rem;
  font-size: 0.9rem;
}
.summary-row.total {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  font-weight: 700;
  font-size: 1rem;
}

/* === TAX / DISCOUNT FIXED SIZE === */
.tax-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  gap: 0.8rem;
}
.tax-row select,
.tax-row input {
  border: 1px solid #374151;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  background: #1f2937;
  color: #f9fafb;
  font-size: 0.9rem;
  transition: all 0.25s ease;
}
.tax-row select:focus,
.tax-row input:focus {
  border-color: #f9fafb;
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
}
.tax-row span {
  background: #374151;
  color: #d1d5db;
  font-weight: 600;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  min-width: 45px;
}

/* === SIDE PANEL === */
.full {
  width: 100%;
}
.divider {
  position: relative;
  text-align: center;
  color: #9ca3af;
  margin: 1.2rem 0;
}
.divider::before,
.divider::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: #e5e7eb;
}
.divider::before {
  left: 0;
}
.divider::after {
  right: 0;
}

.sticky {
  position: sticky;
  top: 110px;   /* below page header */
  z-index: 10;
}

/* === SETTINGS DRAWER === */
.settings-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #fff;
  box-shadow: -6px 0 20px rgba(0, 0, 0, 0.1);
  border-radius: 1rem 0 0 1rem;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 2rem;
  z-index: 1000;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}
.drawer-content section {
  margin-bottom: 1.8rem;
}
.drawer-content h4 {
  margin-bottom: 0.4rem;
  color: #111827;
}
.close-btn {
  border: none;
  background: none;
  font-size: 1.6rem;
  cursor: pointer;
  transition: all 0.25s ease;
}
.close-btn:hover {
  color: #111827;
  transform: rotate(90deg);
}
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition: transform 0.4s ease, opacity 0.3s ease;
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* === BUTTONS === */
.btn-black,
.btn-primary,
.btn-outline,
.btn-black-sm {
  transition: all 0.3s ease;
  font-weight: 500;
}
.btn-black,
.btn-primary {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.4rem;
}
.btn-black:hover,
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-1px);
}
.btn-black-sm {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}
.btn-outline {
  background: #fff;
  border: 1px solid #111827;
  color: #111827;
  border-radius: 10px;
  padding: 0.7rem 1.4rem;
}
.btn-outline:hover {
  background: #111827;
  color: #fff;
  transform: translateY(-1px);
}

/* === MODALS === */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal-card {
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.3s ease;
}
.modal-card h3 {
  font-size: 1.2rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.modal-card .form-group {
  margin-bottom: 1rem;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}
.modal-card input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.7rem;
  font-size: 0.9rem;
  transition: all 0.25s ease;
}
.modal-card input:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
  outline: none;
}

/* === ANIMATIONS === */
.fade-in {
  animation: fadeIn 0.6s ease;
}
.fade-slide-enter-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === RESPONSIVE === */
@media (max-width: 992px) {
  .invoice-layout {
    flex-direction: column;
  }
  .main-pane,
  .side-pane {
    width: 100%;
  }
  .settings-drawer {
    width: 100%;
    border-radius: 1rem 1rem 0 0;
  }
}
/* === Dual-Line Item Card === */
.item-card-dual {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.05);
  transition: all 0.3s ease;
}
.item-card-dual:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}

/* === Top Row (Main Fields) === */
.item-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.item-fields {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.item-fields input {
  width: 80px;
  text-align: right;
}
.amount {
  font-weight: 700;
  color: #111827;
}

/* === Bottom Row (Tax / Discount) === */
.item-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.8rem;
  flex-wrap: wrap;
  gap: 0.8rem;
  border-top: 1px dashed #e5e7eb;
  padding-top: 0.6rem;
}
.item-bottom-row label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 600;
}

.tax-mini,
.discount-mini {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.tax-mini select,
.discount-mini select {
  width: 70px;
  padding: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  background: #f9fafb;
  color: #111827;
}
.tax-mini input,
.discount-mini input {
  width: 80px;
  text-align: right;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.4rem;
  font-size: 0.85rem;
  background: #fff;
  transition: all 0.25s ease;
}
.tax-mini input:focus,
.discount-mini input:focus {
  border-color: #111827;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
}

/* === Remove Button === */
.btn-remove {
  background: transparent;
  border: none;
  color: #ef4444;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
}
.btn-remove:hover {
  color: #dc2626;
  transform: translateY(-1px);
}

/* === Branding Settings Section === */
.branding-settings {
  margin-top: 1.2rem;
}

.branding-settings h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  font-size: 1rem;
  margin-bottom: 1rem;
}

/* Grid layout for logo + color */
.branding-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 1rem;
  align-items: center;
}

/* === Upload Card (Compact Version) === */
.upload-card {
  width: 110px;
  height: 110px;
  border-radius: 12px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.upload-card:hover {
  border-color: #111827;
  background: #f3f4f6;
  transform: translateY(-2px);
}

.upload-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.upload-card:hover img {
  transform: scale(1.05);
}

.upload-card .upload-icon i {
  font-size: 1.6rem;
  color: #6b7280;
}

.upload-card p {
  font-size: 0.8rem;
  color: #9ca3af;
  margin-top: 4px;
}

/* === Color Card === */
.color-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.color-card label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

/* === Color Picker === */
.color-picker {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.05);
}

.color-picker input[type="color"] {
  border: none;
  width: 40px;
  height: 30px;
  cursor: pointer;
  background: transparent;
  padding: 0;
}

.color-code {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Responsive fix for smaller screens */
@media (max-width: 768px) {
  .branding-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  .upload-card {
    margin: 0 auto;
  }
}
/* === 📱 MOBILE RESPONSIVE PATCH FOR ADD INVOICE PAGE === */
@media (max-width: 992px) {
  /* Main layout stack */
  .invoice-layout {
    flex-direction: column !important;
    gap: 1.5rem !important;
  }

  .main-pane,
  .side-pane {
    width: 100% !important;
  }

  /* Sticky panel becomes normal flow */
  .sticky {
    position: relative !important;
    top: auto !important;
  }
}

@media (max-width: 768px) {
  /* Overall padding adjustment */
  .add-invoice-body {
    padding: 1.2rem !important;
    border-radius: 1rem !important;
  }

  /* Header */
  .section-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .section-header h2 {
    font-size: 1.4rem !important;
  }

  .section-header p {
    font-size: 0.9rem !important;
  }

  .btn-help {
    width: 100% !important;
    font-size: 0.9rem !important;
    padding: 0.6rem 1rem !important;
  }

  /* Card sections tighter spacing */
  .card-section {
    padding: 1.2rem !important;
    margin-bottom: 1.2rem !important;
  }

  .card-section h3 {
    font-size: 1rem !important;
  }

  /* Form grid becomes single column */
  .form-grid.two-col .form-group {
    flex: 1 1 100% !important;
  }

  /* Line items - compact stacking */
  .item-card-dual {
    padding: 0.9rem !important;
  }

  .item-top-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.6rem !important;
  }

  .item-fields {
    width: 100% !important;
    justify-content: space-between !important;
  }

  .item-fields input {
    width: 45% !important;
  }

  .amount {
    width: 100% !important;
    text-align: right !important;
  }

  .item-bottom-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.6rem !important;
  }

  .tax-mini,
  .discount-mini {
    width: 100% !important;
    justify-content: space-between !important;
  }

  .btn-remove {
    width: 100% !important;
    justify-content: center !important;
    margin-top: 0.4rem !important;
  }

  /* Add product button */
  .add-product-row {
    justify-content: center !important;
  }

  .btn-outline-sm {
    width: 100% !important;
    text-align: center !important;
  }

  /* Summary */
  .summary {
    padding: 1.2rem !important;
  }

  .summary-row {
    font-size: 0.9rem !important;
  }

  /* Notes */
  textarea {
    font-size: 0.9rem !important;
    padding: 0.6rem !important;
  }

  /* Side buttons */
  .btn-primary.full,
  .btn-outline.full,
  .btn-black.full {
    width: 100% !important;
    justify-content: center !important;
    font-size: 0.95rem !important;
  }

  .divider {
    margin: 1rem 0 !important;
  }

  /* Settings Drawer fills screen on mobile */
  .settings-drawer {
    width: 100% !important;
    border-radius: 1rem 1rem 0 0 !important;
    padding: 1rem 1.2rem !important;
  }

  /* Payment gateway buttons stack */
  .gateway-row {
    flex-direction: column !important;
  }

  .gateway-btn {
    width: 100% !important;
  }

  /* Branding grid becomes single column */
  .branding-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .upload-card {
    width: 100% !important;
    height: auto !important;
  }

  /* Modal adjustments */
  .modal-card {
    width: 90% !important;
    padding: 1.2rem !important;
  }

  .modal-actions {
    flex-direction: column !important;
    gap: 8px !important;
  }

  .modal-actions button {
    width: 100% !important;
  }
}

/* === 📱 Extra Small Devices (≤480px) === */
@media (max-width: 480px) {
  .add-invoice-body {
    padding: 1rem !important;
  }

  .section-header h2 {
    font-size: 1.2rem !important;
  }

  .btn-help {
    font-size: 0.85rem !important;
  }

  .card-section {
    padding: 1rem !important;
  }

  .item-fields input {
    width: 48% !important;
  }

  .gateway-btn {
    font-size: 0.85rem !important;
    padding: 0.7rem 0.8rem !important;
  }

  .color-card,
  .upload-card {
    width: 100% !important;
  }

  .summary-row span,
  .summary-row strong {
    font-size: 0.9rem !important;
  }

  .btn-primary.full,
  .btn-outline.full,
  .btn-black.full {
    font-size: 0.85rem !important;
    padding: 0.7rem 1rem !important;
  }

  .settings-drawer {
    padding: 0.8rem !important;
  }
}
/* === 📱 LINE ITEMS RESPONSIVE PATCH === */
@media (max-width: 992px) {
  .item-card-dual {
    padding: 1rem !important;
    border-radius: 12px !important;
  }

  .item-top-row {
    flex-wrap: wrap !important;
    gap: 0.8rem !important;
  }

  .item-fields {
    width: 100% !important;
    justify-content: space-between !important;
  }

  .item-fields input {
    width: 48% !important;
    text-align: right !important;
  }

  .amount {
    width: 100% !important;
    text-align: right !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    margin-top: 0.3rem !important;
  }

  /* Bottom row adjustments */
  .item-bottom-row {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.6rem !important;
    padding-top: 0.8rem !important;
  }

  .tax-mini,
  .discount-mini {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 0.5rem !important;
    width: 100% !important;
  }

  .tax-mini label,
  .discount-mini label {
    min-width: 70px !important;
    font-size: 0.8rem !important;
  }

  .tax-mini select,
  .discount-mini select {
    flex: 1 1 auto !important;
    min-width: 65px !important;
  }

  .tax-mini input,
  .discount-mini input {
    flex: 1 1 auto !important;
    width: 70px !important;
    text-align: right !important;
  }

  .btn-remove {
    width: 100% !important;
    justify-content: center !important;
    margin-top: 0.4rem !important;
    padding: 0.6rem 1rem !important;
    border-radius: 8px !important;
    font-size: 0.9rem !important;
    background: #fee2e2 !important;
    color: #991b1b !important;
  }

  .btn-remove:hover {
    background: #fecaca !important;
    color: #7f1d1d !important;
  }
}

@media (max-width: 600px) {
  .item-card-dual {
    padding: 0.9rem !important;
  }

  .item-top-row {
    flex-direction: column !important;
    gap: 0.6rem !important;
  }

  .item-fields {
    flex-direction: row !important;
    justify-content: space-between !important;
  }

  .item-fields input {
    width: 45% !important;
  }

  .tax-mini,
  .discount-mini {
    flex-wrap: wrap !important;
    gap: 0.4rem !important;
  }

  .tax-mini input,
  .discount-mini input {
    width: 100% !important;
  }

  .btn-remove {
    font-size: 0.85rem !important;
    padding: 0.5rem 0.8rem !important;
  }
}

@media (max-width: 480px) {
  .item-card-dual {
    padding: 0.8rem !important;
  }

  .item-fields input {
    width: 47% !important;
  }

  .amount {
    font-size: 0.9rem !important;
  }

  .tax-mini label,
  .discount-mini label {
    font-size: 0.75rem !important;
  }

  .tax-mini select,
  .discount-mini select,
  .tax-mini input,
  .discount-mini input {
    font-size: 0.85rem !important;
    padding: 0.4rem !important;
  }

  .btn-remove {
    font-size: 0.85rem !important;
    padding: 0.45rem 0.8rem !important;
  }
}
/* === 📱 PATCH: Make Settings Drawer Scrollable on Mobile === */
@media (max-width: 992px) {
  .settings-drawer {
    height: 100vh !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    padding-bottom: 2rem !important;
  }

  /* Fix for drawer content spacing on mobile */
  .drawer-content {
    display: flex !important;
    flex-direction: column !important;
    gap: 1.5rem !important;
  }

  /* Add subtle gradient fade at bottom to indicate scroll */
  .settings-drawer::after {
    content: "";
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(to top, rgba(255, 255, 255, 0.9), transparent);
    pointer-events: none;
  }

  /* Ensure header stays visible at top */
  .drawer-header {
    position: sticky !important;
    top: 0 !important;
    background: #fff !important;
    z-index: 10 !important;
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
    border-bottom: 1px solid #e5e7eb !important;
  }
}

@media (max-width: 600px) {
  .settings-drawer {
    width: 100% !important;
    border-radius: 1rem 1rem 0 0 !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08) !important;
  }

  .drawer-content {
    gap: 1.2rem !important;
  }
}
/* === 🧾 PATCH: Fix Line Items Layout on Desktop === */
@media (min-width: 992px) {
  /* Properly align top row fields */
  .item-top-row {
    display: grid !important;
    grid-template-columns: 1.6fr 1.2fr auto !important;
    align-items: center !important;
    gap: 1rem !important;
  }

  /* Input fields (Qty, Rate, Amount) stay aligned inline */
  .item-fields {
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 0.8rem !important;
  }

  .item-fields input {
    width: 90px !important;
    text-align: right !important;
  }

  .item-fields .amount {
    min-width: 100px !important;
    text-align: right !important;
    font-weight: 700 !important;
    color: #111827 !important;
  }

  /* Make select width uniform for item names */
  .custom-select select {
    width: 100% !important;
    min-width: 180px !important;
  }

  /* Fix bottom row layout (Tax, Discount, Remove) */
  .item-bottom-row {
    display: grid !important;
    grid-template-columns: repeat(3, auto) 1fr !important;
    align-items: center !important;
    gap: 1.2rem !important;
  }

  .item-bottom-row .tax-mini,
  .item-bottom-row .discount-mini {
    display: flex !important;
    align-items: center !important;
    gap: 0.4rem !important;
  }

  /* Prevent "Remove" button from jumping */
  .item-bottom-row .btn-remove {
    justify-self: end !important;
  }
}

/* === 📱 Keep Mobile View Untouched === */
@media (max-width: 991px) {
  .item-top-row {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
  .item-fields {
    width: 100% !important;
    justify-content: space-between !important;
  }
}
/* === 🌀 GLOBAL LOADERS & DISABLED STATES === */
.loader-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid #d1d5db;
  border-top-color: #111827;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

/* === BUTTON DISABLED / LOADING === */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  filter: grayscale(1);
}
button.loading {
  position: relative;
  color: transparent !important;
}
button.loading::after {
  content: "";
  position: absolute;
  inset: 0;
  margin: auto;
  width: 18px;
  height: 18px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* === EMPTY STATE PLACEHOLDERS === */
.empty-state {
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  text-align: center;
  padding: 2rem 1.5rem;
  color: #6b7280;
  margin-top: 1rem;
  transition: all 0.25s ease;
}
.empty-state:hover {
  border-color: #9ca3af;
  background: #f3f4f6;
}
.empty-state i {
  font-size: 2rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

/* === CLIENT / PRODUCT LIST LOADER === */
.inline-loader {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: #6b7280;
}
.inline-loader .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #111827;
  animation: pulse 0.9s ease-in-out infinite;
}
.inline-loader .dot:nth-child(2) {
  animation-delay: 0.2s;
}
.inline-loader .dot:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes pulse {
  0%, 100% {
    opacity: 0.2;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* === SUCCESS ANIMATION === */
.toast-success-anim {
  animation: bounceIn 0.4s ease;
}
@keyframes bounceIn {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  60% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
}

/* === FORM INPUT FOCUS ENHANCEMENT === */
input:focus,
select:focus,
textarea:focus {
  border-color: #111827 !important;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1) !important;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

/* === TRANSITION ENHANCEMENTS === */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease-in-out;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* === MODAL TRANSITION PATCH === */
.modal-overlay {
  animation: fadeIn 0.3s ease;
}
.modal-card {
  animation: slideUp 0.35s ease;
}
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === SETTINGS DRAWER IMPROVEMENT === */
.settings-drawer {
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.08) !important;
  transition: transform 0.3s ease, opacity 0.3s ease !important;
}
.settings-drawer.open {
  transform: translateX(0);
}
.settings-drawer.closing {
  transform: translateX(100%);
  opacity: 0.8;
}

/* === IMPROVED HOVER STATES === */
.btn-primary:hover,
.btn-black:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.15);
}
.btn-outline:hover {
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.08);
}

/* === SMALL SCREEN PATCH: Prevent Scroll Jumps === */
@media (max-width: 992px) {
  body {
    overflow-x: hidden !important;
  }
  .settings-drawer {
    transition: all 0.35s ease !important;
  }
}

/* === OPTIONAL: FINE SHADOW FOR CARDS === */
.card-section {
  box-shadow: 0 3px 10px rgba(17, 24, 39, 0.04);
}
.card-section:hover {
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}

/* === SCROLLBAR BEAUTIFY === */
::-webkit-scrollbar {
  width: 7px;
}
::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* === FULLSCREEN PREVIEW POPUP === */
.preview-fullscreen {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  backdrop-filter: blur(4px);
  padding: 20px;
}

.preview-content {
  position: relative;
  width: 100%;
  max-width: 1000px;
  max-height: 95vh;
  background: #fff;
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  animation: zoomIn 0.25s ease;
  padding: 20px;
}

.preview-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #111827;
  color: #fff;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.25s;
}

.preview-close-btn:hover {
  background: #000;
  transform: scale(1.1);
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity .25s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>

